<template>
  <div class="dashboard-container">
    <!-- Page Header -->
    <div class="header">
      <h1 class="title">{{ $t('dashboard.title') }}</h1>
      <el-button type="primary" @click="refreshAllData" icon="Refresh">{{ $t('common.refresh') }}</el-button>
    </div>

    <!-- Stats -->
    <div class="stats-grid">
      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon expense-icon">
            <el-icon><Money /></el-icon>
          </div>
          <div class="stat-info">
            <h3 class="stat-title">{{ $t('dashboard.stats.totalExpense') }}</h3>
            <p class="stat-value">{{ formatCurrency(stats.totalExpense) }}</p>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon category-icon">
            <el-icon><Folder /></el-icon>
          </div>
          <div class="stat-info">
            <h3 class="stat-title">{{ $t('dashboard.stats.categories') }}</h3>
            <p class="stat-value">{{ stats.totalCategories }}</p>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon expense-count-icon">
            <el-icon><List /></el-icon>
          </div>
          <div class="stat-info">
            <h3 class="stat-title">{{ $t('dashboard.stats.expenseCount') }}</h3>
            <p class="stat-value">{{ stats.totalExpenseCount }}</p>
          </div>
        </div>
      </el-card>

      <el-card class="stat-card">
        <div class="stat-content">
          <div class="stat-icon accounts-icon">
            <el-icon><UserFilled /></el-icon>
          </div>
          <div class="stat-info">
            <h3 class="stat-title">{{ $t('dashboard.stats.accountHolders') }}</h3>
            <p class="stat-value">{{ stats.totalAccountHolders }}</p>
          </div>
        </div>
      </el-card>
    </div>

    <!-- Quarter Chart -->
    <el-card class="chart-card">
      <template #header>
        <div class="card-header">
          <div class="title-with-select">
            <span>{{ $t('dashboard.chart.quarterlyExpenses') }}</span>
            <el-select v-model="chartYear" @change="handleChartYearChange" size="small" style="width: 120px; margin-left: 10px;">
              <el-option 
                v-for="year in availableYears" 
                :key="year" 
                :label="year" 
                :value="year"
              />
            </el-select>
          </div>
          <div class="header-actions">
            <el-button type="primary" plain size="small" @click="$router.push('/report')">
              {{ $t('common.viewDetailedReport') }}
            </el-button>
          </div>
        </div>
      </template>
      <div class="chart-container">
        <div class="chart-title">{{ $t('dashboard.chart.year') }} {{ chartYear }}</div>
        <Bar
          v-if="quarterData"
          :options="chartOptions"
          :data="quarterData"
        />
        <div v-else class="no-data">
          <el-empty :description="$t('common.noData')" />
        </div>
      </div>
    </el-card>

    <!-- Recent Activity -->
    <el-card class="activity-card">
      <template #header>
        <div class="card-header">
          <div class="title-with-select">
            <span>{{ $t('dashboard.chart.recentExpenses') }}</span>
            <el-select v-model="selectedYear" @change="handleYearChange" size="small" style="width: 120px; margin-left: 10px;">
              <el-option 
                v-for="year in availableYears" 
                :key="year" 
                :label="year" 
                :value="year"
              />
            </el-select>
          </div>
          <div class="header-actions">
            <el-radio-group v-model="selectedMonth" @change="handleMonthChange" size="small">
              <el-radio-button v-for="month in availableMonths" :key="month.value" :label="month.value" :value="month.value">
                {{ month.label }}
              </el-radio-button>
            </el-radio-group>
            <el-button type="primary" plain size="small" @click="$router.push('/expenses')">
              {{ $t('common.viewAll') }}
            </el-button>
          </div>
        </div>
      </template>
      <!-- Month selection buttons -->
      <div class="period-indicator">
        <el-tag type="info" size="large">{{ selectedPeriodLabel }}</el-tag>
      </div>
      <el-table :data="recentExpenses" stripe style="width: 100%">
        <template #empty>
          <el-empty :description="$t('dashboard.chart.noExpensesInMonth')" />
        </template>
        <el-table-column prop="payment_date" :label="$t('dashboard.table.paymentDate')" width="130" />
        <el-table-column prop="expense_type" :label="$t('dashboard.table.expenseType')" width="150">
          <template #default="scope">
            {{ scope?.row?.expense_type || $t('common.undefined') }}
          </template>
        </el-table-column>
        <el-table-column prop="category" :label="$t('dashboard.table.category')" width="150" />
        <el-table-column prop="description" :label="$t('dashboard.table.description')" show-overflow-tooltip />
        <el-table-column prop="amount" :label="$t('dashboard.table.amount')" width="130">
          <template #default="scope">
            {{ scope?.row ? formatCurrency(scope.row.amount) : '' }}
          </template>
        </el-table-column>
        <el-table-column prop="account_holder" :label="$t('dashboard.table.accountHolder')" width="150" />
      </el-table>
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :page-sizes="[10, 20, 30, 40, 50]"
          layout="sizes, prev, pager, next"
          :total="totalExpenses"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Money, Folder, List, UserFilled } from '@element-plus/icons-vue'
import { Bar } from 'vue-chartjs'
import { useI18n } from 'vue-i18n'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
} from 'chart.js'
import axios from 'axios'
import { AxiosError } from 'axios'
import { getDaysInMonth, lastDayOfMonth } from 'date-fns'
import { API_URLS, API_VERSION } from '@/config'

// Register ChartJS components
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// Initialize i18n
const { t } = useI18n()

interface Expense {
  id: string;
  payment_date: string;
  expense_type?: string;
  category: string;
  description?: string;
  amount: number;
  account_holder: string;
}

interface QuarterData {
  quarter: string;
  year: string;
  total_amount: number;
}

interface DashboardStats {
  totalExpense: number;
  totalCategories: number;
  totalExpenseCount: number;
  totalAccountHolders: number;
}

// State
const recentExpenses = ref<Expense[]>([])
const quarterlyExpenses = ref<QuarterData[]>([])
const totalExpenses = ref(0)
const pagination = ref({
  currentPage: 1,
  pageSize: 10
})
const stats = ref<DashboardStats>({
  totalExpense: 0,
  totalCategories: 0,
  totalExpenseCount: 0,
  totalAccountHolders: 0
})

// State for year and month selection
const today = new Date()
const currentMonth = today.getMonth()
const currentYear = today.getFullYear()
const selectedYear = ref(currentYear)
const selectedMonth = ref((currentMonth + 1).toString().padStart(2, '0'))

// Available years (current year and 4 previous years)
const availableYears = computed(() => {
  const years = []
  const maxYear = currentYear // Restrict to current year (no future years)
  for (let i = 0; i < 5; i++) {
    years.push(maxYear - i)
  }
  return years
})

// Available months for the selected year
const availableMonths = computed(() => {
  const months = []
  // For current year, only show months up to the current month
  const monthLimit = selectedYear.value === currentYear ? currentMonth + 1 : 12
  
  for (let i = 1; i <= monthLimit; i++) {
    const monthValue = i.toString().padStart(2, '0')
    const monthDate = new Date(selectedYear.value, i - 1, 1)
    const monthLabel = new Intl.DateTimeFormat('vi-VN', { month: 'short' }).format(monthDate)
    months.push({ value: monthValue, label: monthLabel })
  }
  return months
})

// Add after your existing computed properties
const daysInMonth = computed(() => {
  return getDaysInMonth(new Date(selectedYear.value, parseInt(selectedMonth.value) - 1));
});

// Utility functions
const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined || amount === null) return '0 ₫'
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount)
}

// Chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.dataset.label || ''
          const value = context.raw
          return `${label}: ${formatCurrency(value)}`
        },
      },
    },
  },
}

// Quarter chart data
const quarterData = computed(() => {
  if (!quarterlyExpenses.value || quarterlyExpenses.value.length === 0) return null
  
  return {
    labels: quarterlyExpenses.value.map(item => `${item.quarter} (${item.year})`),
    datasets: [
      {
        label: t('expenses.title'),
        data: quarterlyExpenses.value.map(item => item.total_amount || 0),
        backgroundColor: 'rgba(64, 158, 255, 0.6)',
        borderColor: 'rgba(64, 158, 255, 1)',
        borderWidth: 1,
      },
    ],
  }
})

// Pagination handlers
const handleSizeChange = (size: number) => {
  pagination.value.pageSize = size
  fetchRecentExpenses()
}

const handleCurrentChange = (page: number) => {
  pagination.value.currentPage = page
  fetchRecentExpenses()
}

// Year selection handler
const handleYearChange = (year: number) => {
  // If switching to a different year, set month to December 
  // unless it's the current year (then set to current month)
  if (year === currentYear) {
    selectedMonth.value = (currentMonth + 1).toString().padStart(2, '0')
  } else {
    // For past years, default to December (last month of the year)
    selectedMonth.value = '12'
  }
  pagination.value.currentPage = 1 // Reset pagination
  fetchRecentExpenses()
}

// Month selection handler
const handleMonthChange = (month: string) => {
  pagination.value.currentPage = 1 // Reset to first page when changing month
  fetchRecentExpenses()
}

// Add error message translations
const messages = {
  error: {
    loadingRecentExpenses: 'Không thể tải dữ liệu chi phí gần đây',
    loadingQuarterlyExpenses: 'Không thể tải dữ liệu chi phí theo quý',
    loadingStats: 'Không thể tải dữ liệu thống kê',
    futureYearWarning: 'Đã chuyển sang năm hiện tại vì không thể truy vấn dữ liệu trong tương lai',
    refreshData: 'Không thể tải lại dữ liệu'
  },
  info: {
    refreshing: 'Đang tải lại dữ liệu...',
    refreshSuccess: 'Dữ liệu đã được cập nhật'
  }
}

// Update fetchRecentExpenses to use translations
const fetchRecentExpenses = async () => {
  try {
    console.log(`Fetching expenses for ${selectedYear.value}-${selectedMonth.value}`)
    
    // Get the expenses for the selected period
    const startDate = `${selectedYear.value}-${selectedMonth.value}-01`
    const endDate = `${selectedYear.value}-${selectedMonth.value}-${String(daysInMonth.value).padStart(2, '0')}`
    
    // Fetch all expenses for the selected period
    const allResponse = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        page_size: 1000, // Get all expenses to handle pagination client-side
      }
    })
    
    // Handle both new and old response structures
    let allExpenses = [];
    let total = 0;
    
    if (allResponse.data && allResponse.data.expenses) {
      // New response structure with { expenses: [...], pagination: {...} }
      allExpenses = allResponse.data.expenses;
      total = allResponse.data.pagination.total;
    } else if (allResponse.data && Array.isArray(allResponse.data)) {
      // Old response structure (direct array)
      allExpenses = allResponse.data;
      total = allExpenses.length;
    } else if (allResponse.data && allResponse.data.items) {
      // Another possible structure with { items: [...], total: number }
      allExpenses = allResponse.data.items;
      total = allResponse.data.total;
    } else {
      // Fallback to empty array
      allExpenses = [];
      total = 0;
    }
    
    totalExpenses.value = total;
    console.log(`Found ${totalExpenses.value} expenses for the selected period`);
    
    // Calculate start and end indexes for pagination
    const startIndex = (pagination.value.currentPage - 1) * pagination.value.pageSize;
    const endIndex = startIndex + pagination.value.pageSize;
    
    // Slice the array to get the current page of expenses
    recentExpenses.value = allExpenses.slice(startIndex, endIndex);
  } catch (error) {
    console.error('Error fetching recent expenses:', error)
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        console.error('Error response data:', axiosError.response.data)
        console.error('Error response status:', axiosError.response.status)
        console.error('Error response headers:', axiosError.response.headers)
      } else if (axiosError.request) {
        console.error('Error request:', axiosError.request)
      } else {
        console.error('Error message:', axiosError.message)
      }
    }
    ElMessage.error(t('error.loadingRecentExpenses'))
  }
}

// Add chartYear state variable
const chartYear = ref(currentYear)

// Handle chart year change
const handleChartYearChange = (year: number) => {
  chartYear.value = year
  fetchQuarterlyExpenses()
}

// Update fetchQuarterlyExpenses to use translations
const fetchQuarterlyExpenses = async () => {
  try {
    // Ensure we're not querying the future
    const now = new Date()
    if (chartYear.value > now.getFullYear()) {
      console.warn(`Preventing query with future year: ${chartYear.value}. Using current year instead.`)
      // Use current year
      chartYear.value = now.getFullYear()
      
      // Update the UI to reflect the change
      ElMessage.warning(t('error.futureYearWarning'))
    }
    
    // Use the selected chart year
    const url = `${API_URLS.report}/${API_VERSION}/report?year=${chartYear.value}&filter_by_year=true`
    console.log(`Fetching quarterly data for year ${chartYear.value}`)
    console.log(`API request URL: ${url}`)
    
    const response = await axios.get(url)
    
    // Log the raw response data to check what's being returned
    console.log('Raw quarterly data response:', response.data)
    
    if (response.data && response.data.byQuarter) {
      // Filter data to include only the selected year's quarters
      const filteredData = response.data.byQuarter.filter((item: QuarterData) => 
        item.year === chartYear.value.toString()
      )
      
      // Sort quarters in correct order (Q1, Q2, Q3, Q4)
      const sortedData = filteredData.sort((a: QuarterData, b: QuarterData) => {
        const getQuarterNumber = (q: string) => parseInt(q.replace('Q', ''))
        return getQuarterNumber(a.quarter) - getQuarterNumber(b.quarter)
      })
      
      console.log('Filtered and sorted quarterly data:', sortedData)
      quarterlyExpenses.value = sortedData
    } else {
      console.warn('No quarterly data received from API')
      quarterlyExpenses.value = []
    }
  } catch (error) {
    console.error('Error fetching quarterly expenses:', error)
    ElMessage.error(t('error.loadingQuarterlyExpenses'))
  }
}

// Update fetchStats to use translations
const fetchStats = async () => {
  try {
    // Get total expense amount for all time
    const reportResponse = await axios.get(`${API_URLS.report}/${API_VERSION}/report`)
    if (reportResponse?.data?.byQuarter) {
      stats.value.totalExpense = reportResponse.data.byQuarter.reduce(
        (total: number, quarter: QuarterData) => total + (quarter?.total_amount || 0), 
        0
      )
    }
    
    // Get total expense count and unique account holders
    const expensesResponse = await axios.get(`${API_URLS.expenses}/${API_VERSION}/expenses/all`)
    let expenses = []
    
    if (expensesResponse?.data?.expenses) {
      // New response structure
      expenses = expensesResponse.data.expenses
    } else if (Array.isArray(expensesResponse?.data)) {
      // Old response structure
      expenses = expensesResponse.data
    }
    
    if (Array.isArray(expenses)) {
      stats.value.totalExpenseCount = expenses.length
      
      // Get unique account holders
      const accountHolders = new Set(expenses
        .filter((expense: Expense) => expense && expense.account_holder)
        .map((expense: Expense) => expense.account_holder))
      stats.value.totalAccountHolders = accountHolders.size
    }
    
    // Get total categories
    const categoriesResponse = await axios.get(`${API_URLS.categories}/${API_VERSION}/categories`)
    if (categoriesResponse?.data) {
      stats.value.totalCategories = categoriesResponse.data.length
    }
  } catch (error) {
    console.error('Error fetching stats:', error)
    ElMessage.error(t('error.loadingStats'))
  }
}

// Add a computed property for the selected period description
const selectedPeriodLabel = computed(() => {
  // Format to display like "Tháng 7, 2023"
  const month = parseInt(selectedMonth.value)
  const date = new Date(selectedYear.value, month - 1, 1)
  return new Intl.DateTimeFormat('vi-VN', { month: 'long', year: 'numeric' }).format(date)
})

// Fetch all data on component mount
onMounted(async () => {
  // Set defaults for the current year and month
  selectedYear.value = currentYear
  selectedMonth.value = (currentMonth + 1).toString().padStart(2, '0')
  chartYear.value = currentYear
  
  // Fetch data with current selections
  await Promise.all([
    fetchRecentExpenses(),
    fetchQuarterlyExpenses(),
    fetchStats()
  ])

  // Remove or comment these debug log statements that are causing the error
  // console.log("i18n locale:", (useI18n()).locale.value)
  // console.log("Dashboard title translation:", t('dashboard.title'))
  // console.log("Dashboard stats translation:", t('dashboard.stats.totalExpense'))
})

// Update refreshAllData to use translations
const refreshAllData = async () => {
  ElMessage.info(t('info.refreshing'))
  try {
    await Promise.all([
      fetchRecentExpenses(),
      fetchQuarterlyExpenses(),
      fetchStats()
    ])
    ElMessage.success(t('info.refreshSuccess'))
  } catch (error) {
    console.error('Error refreshing data:', error)
    ElMessage.error(t('error.refreshData'))
  }
}
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  font-size: 24px;
  font-weight: bold;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 10px;
}

.stat-card {
  transition: transform 0.3s;
}

.stat-card:hover {
  transform: translateY(-5px);
}

.stat-content {
  display: flex;
  align-items: center;
  padding: 10px;
}

.stat-icon {
  font-size: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.expense-icon {
  background-color: rgba(64, 158, 255, 0.1);
  color: #409EFF;
}

.category-icon {
  background-color: rgba(103, 194, 58, 0.1);
  color: #67C23A;
}

.expense-count-icon {
  background-color: rgba(230, 162, 60, 0.1);
  color: #E6A23C;
}

.accounts-icon {
  background-color: rgba(144, 147, 153, 0.1);
  color: #909399;
}

.stat-info {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #909399;
  margin: 0 0 5px 0;
}

.stat-value {
  font-size: 20px;
  font-weight: bold;
  margin: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.activity-card {
  margin-bottom: 10px;
}

.chart-card {
  margin-bottom: 10px;
}

.chart-container {
  height: 300px;
  position: relative;
}

.no-data {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 0 20px;
}

.period-indicator {
  margin: 10px 20px;
  display: flex;
  justify-content: center;
}

.chart-title {
  text-align: center;
  font-weight: bold;
  margin-bottom: 15px;
  font-size: 16px;
  color: #606266;
}

.title-with-select {
  display: flex;
  align-items: center;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style> 